import { Key } from 'react';
import { MdOutlineFilterAlt, MdOutlineTune } from 'react-icons/md';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, IconButton, TmdxSwitch } from '@ds';
import { WorkdaySyncData } from '@lp/features/Scheduler/types';
import { formatDate } from '@/utils/helpers';

type FilterOptionsDropdownProps = {
  onlyStatusFilter?: boolean;
  withShiftTotals?: boolean;
  shiftTotalsEnabled?: boolean;
  onShiftTotalsChange?: (enabled: boolean) => void;
  statusFilterEnabled: boolean;
  onStatusFilterChange: (enabled: boolean) => void;
  workdayData?: WorkdaySyncData;
};

enum ActionKey {
  ShiftTotals = 'ShiftTotals',
  StatusFilter = 'StatusFilter',
  WorkdaySync = 'WorkdaySync',
}

export const FilterOptionsDropdown = ({
  onlyStatusFilter,
  withShiftTotals,
  shiftTotalsEnabled,
  onShiftTotalsChange,
  statusFilterEnabled,
  onStatusFilterChange,
  workdayData,
}: FilterOptionsDropdownProps) => {
  const onSelectionChange = (key: Key) => {
    switch (key) {
      case ActionKey.ShiftTotals:
        onShiftTotalsChange?.(!shiftTotalsEnabled);
        break;
      case ActionKey.StatusFilter:
        onStatusFilterChange(!statusFilterEnabled);
        break;
      case ActionKey.WorkdaySync:
        workdayData?.onWorkdaySync();
        break;
      default:
        break;
    }
  };

  if (onlyStatusFilter) {
    return (
      <IconButton
        size="md"
        disableRipple
        variant={statusFilterEnabled ? 'outline' : 'filled'}
        startContent={<MdOutlineFilterAlt size={16} />}
        onClick={() => onStatusFilterChange(!statusFilterEnabled)}
      />
    );
  }

  return (
    <Dropdown placement="bottom-end" closeOnSelect={false}>
      <DropdownTrigger>
        <Button
          size="sm"
          variant="light"
          disableRipple
          className="bg-base-55 ps-4 pe-2"
          endContent={<MdOutlineTune className="text-base-30" />}
        >
          <span className="text-m-m text-base-10">Options</span>
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Options List"
        onAction={onSelectionChange}
        disabledKeys={workdayData?.isDisabled ? [ActionKey.WorkdaySync] : undefined}
      >
        {withShiftTotals ? (
          <DropdownItem
            key={ActionKey.ShiftTotals}
            endContent={
              <TmdxSwitch isReadOnly size="sm" isSelected={shiftTotalsEnabled} classNames={{ wrapper: 'mr-0' }} />
            }
          >
            Show Shift Totals
          </DropdownItem>
        ) : (
          (null as never)
        )}
        <DropdownItem
          key={ActionKey.StatusFilter}
          showDivider={!!workdayData}
          endContent={
            <TmdxSwitch isReadOnly size="sm" isSelected={statusFilterEnabled} classNames={{ wrapper: 'mr-0' }} />
          }
        >
          Show Status Filtering
        </DropdownItem>
        {workdayData ? (
          <DropdownItem
            key={ActionKey.WorkdaySync}
            description={
              workdayData.lastSyncTime ? formatDate(workdayData.lastSyncTime, 'MM.dd.yyyy HH:mm') : undefined
            }
          >
            Sync with Workday
          </DropdownItem>
        ) : (
          (null as never)
        )}
      </DropdownMenu>
    </Dropdown>
  );
};
